import React from "react";

import SectionContainer from "../components/common/SectionContainer";
import CTASection from "../components/sections/CTASection";
import Seo from "../seo/Seo";
import pageMeta from "../data/pageMeta";

function AboutPage() {
  const principles = [
    {
      number: "01",
      title: "Strategy First",
      description:
        "Every engagement begins with business objectives, market realities and growth opportunities before tactics are discussed."
    },
    {
      number: "02",
      title: "Technology Enabled",
      description:
        "Modern businesses need scalable systems, automation and digital infrastructure that support long-term growth."
    },
    {
      number: "03",
      title: "Execution Obsessed",
      description:
        "Ideas are only valuable when implemented. We focus on turning strategy into measurable outcomes."
    },
    {
      number: "04",
      title: "Built For Sustainable Growth",
      description:
        "We prioritize long-term value creation over short-term wins and vanity metrics."
    }
  ];

  const capabilities = [
    "Growth Strategy",
    "Demand Generation",
    "Digital Ecosystems",
    "Marketing Operations",
    "Web Platforms",
    "Business Automation"
  ];

  return (
    <>
      <Seo
        title={pageMeta.about.title}
        description={pageMeta.about.description}
      />

      {/* HERO */}

      <section
        style={{
          background:
            "linear-gradient(180deg,#00152d 0%, #002147 100%)",
          color: "#fff",
          padding: "140px 24px 120px",
          textAlign: "center"
        }}
      >
        <div
          style={{
            maxWidth: "1000px",
            margin: "0 auto"
          }}
        >
          <span
            style={{
              color: "#21b573",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px"
            }}
          >
            About EkritFly
          </span>

          <h1
            style={{
              fontSize: "clamp(3.5rem,6vw,6rem)",
              lineHeight: 0.95,
              marginTop: "24px",
              marginBottom: "28px",
              fontWeight: 700
            }}
          >
            Building Growth Systems
            <br />
            For Ambitious Businesses.
          </h1>

          <p
            style={{
              maxWidth: "760px",
              margin: "0 auto",
              color: "rgba(255,255,255,.75)",
              fontSize: "1.15rem",
              lineHeight: 1.9
            }}
          >
            We believe sustainable growth happens when
            strategy, marketing and technology operate
            as one connected system.
          </p>
        </div>
      </section>

      {/* PHILOSOPHY */}

      <SectionContainer background="#ffffff">
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            display: "flex",
            gap: "80px",
            flexWrap: "wrap"
          }}
        >
          <div style={{ flex: 1, minWidth: "320px" }}>
            <span
              style={{
                color: "#21b573",
                fontWeight: 700,
                textTransform: "uppercase"
              }}
            >
              Our Philosophy
            </span>

            <h2
              style={{
                color: "#002147",
                fontSize: "clamp(2.5rem,4vw,4rem)",
                lineHeight: 1.05,
                marginTop: "20px"
              }}
            >
              Growth Is Not
              <br />
              A Marketing Problem.
            </h2>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: "320px",
              color: "#6e6e73",
              lineHeight: 2
            }}
          >
            <p>
              Most organizations already have great
              products, talented teams and ambitious goals.
            </p>

            <p>
              The challenge is alignment. When strategy,
              marketing and technology operate independently,
              growth becomes fragmented.
            </p>

            <p>
              When they operate together, growth compounds.
            </p>
          </div>
        </div>
      </SectionContainer>

      {/* WHY WE EXIST */}

      <SectionContainer background="#f8fafc">
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            textAlign: "center"
          }}
        >
          <span
            style={{
              color: "#21b573",
              fontWeight: 700,
              textTransform: "uppercase"
            }}
          >
            Why We Exist
          </span>

          <h2
            style={{
              color: "#002147",
              fontSize: "clamp(2.5rem,4vw,4rem)",
              marginTop: "20px",
              marginBottom: "30px"
            }}
          >
            Businesses Don't Need
            More Tactics.
          </h2>

          <p
            style={{
              color: "#6e6e73",
              lineHeight: 2,
              fontSize: "1.05rem"
            }}
          >
            They need clarity, systems and execution.
            EkritFly was created to help organizations
            bridge the gap between business strategy
            and digital execution.
          </p>
        </div>
      </SectionContainer>

      {/* APPROACH */}

      <SectionContainer background="#ffffff">
        <div
          style={{
            textAlign: "center",
            marginBottom: "60px"
          }}
        >
          <span
            style={{
              color: "#21b573",
              fontWeight: 700,
              textTransform: "uppercase"
            }}
          >
            The EkritFly Approach
          </span>

          <h2
            style={{
              color: "#002147",
              fontSize: "clamp(2.5rem,4vw,4rem)",
              marginTop: "20px"
            }}
          >
            How We Think.
            <br />
            How We Work.
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit,minmax(280px,1fr))",
            gap: "24px"
          }}
        >
          {principles.map((item) => (
            <div
              key={item.title}
              className="premium-card"
              style={{
                padding: "36px"
              }}
            >
              <div
                style={{
                  color: "#21b573",
                  fontSize: "2rem",
                  fontWeight: 700,
                  marginBottom: "20px"
                }}
              >
                {item.number}
              </div>

              <h3
                style={{
                  color: "#002147",
                  marginBottom: "16px"
                }}
              >
                {item.title}
              </h3>

              <p
                style={{
                  color: "#6e6e73",
                  lineHeight: 1.9
                }}
              >
                {item.description}
              </p>
            </div>
          ))}
        </div>
      </SectionContainer>

      {/* WHAT WE BUILD */}

      <SectionContainer background="#f8fafc">
        <div
          style={{
            textAlign: "center",
            maxWidth: "1000px",
            margin: "0 auto"
          }}
        >
          <span
            style={{
              color: "#21b573",
              fontWeight: 700,
              textTransform: "uppercase"
            }}
          >
            What We Build
          </span>

          <h2
            style={{
              color: "#002147",
              fontSize: "clamp(2.5rem,4vw,4rem)",
              marginTop: "20px",
              marginBottom: "60px"
            }}
          >
            Systems Designed
            For Sustainable Growth.
          </h2>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "18px"
            }}
          >
            {capabilities.map((item) => (
              <div
                key={item}
                style={{
                  padding: "14px 24px",
                  borderRadius: "999px",
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  color: "#002147",
                  fontWeight: 600
                }}
              >
                {item}
              </div>
            ))}
          </div>
        </div>
      </SectionContainer>

      <CTASection />
    </>
  );
}

export default AboutPage;